import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { apiClient } from '@/common/lib';
import { useBodyScrollLock } from '@/common/hooks';

/**
 * ImagePreviewModal - Fullscreen modal for previewing images.
 * Visually consistent with Lead Details -> Attachments Preview.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {string} props.imageUrl - Image URL to display
 * @param {string} [props.title] - Image or product title
 * @param {() => void} props.onClose - Close callback
 */
export function ImagePreviewModal({
  isOpen,
  imageUrl,
  title = 'Product Image',
  onClose,
}) {
  useBodyScrollLock(isOpen);

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const backdropRef = useRef(null);
  const pointerDownOnBackdrop = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setHasError(false);
    }
  }, [isOpen, imageUrl]);

  // Handle ESC key press
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !imageUrl || typeof document === 'undefined') return null;

  const handleDownload = async () => {
    if (!imageUrl) return;
    const fileName = title
      ? `${title.toLowerCase().replace(/[^a-z0-9_-]/g, '_')}.png`
      : 'product-image.png';
    const loadingToastId = toast.loading('Downloading image...');

    try {
      let blob;
      if (imageUrl.startsWith('blob:') || imageUrl.startsWith('data:')) {
        const response = await fetch(imageUrl);
        blob = await response.blob();
      } else {
        try {
          const response = await apiClient.get(imageUrl, {
            responseType: 'blob',
          });
          blob = new Blob([response.data], {
            type: response.headers['content-type'] || 'image/png',
          });
        } catch {
          const response = await fetch(imageUrl);
          if (!response.ok) throw new Error('Fetch failed');
          blob = await response.blob();
        }
      }

      const localUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = localUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(localUrl), 1000);

      toast.success('Download complete!', { id: loadingToastId });
    } catch (err) {
      console.error('Download failed: ', err);
      try {
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = fileName;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Opened in new tab', { id: loadingToastId });
      } catch {
        toast.error('Download failed', { id: loadingToastId });
      }
    }
  };

  const handleBackdropMouseDown = (e) => {
    pointerDownOnBackdrop.current = e.target === backdropRef.current;
  };

  const handleBackdropMouseUp = (e) => {
    if (pointerDownOnBackdrop.current && e.target === backdropRef.current) {
      onClose?.();
    }
    pointerDownOnBackdrop.current = false;
  };

  const modalContent = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Image Preview'}
      className="fixed inset-0 z-[9999] bg-[#111b21] flex flex-col justify-between overflow-hidden select-none animate-fadeIn"
    >
      {/* Top Bar Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#111b21] text-gray-200 border-b border-gray-800/60 z-10 shrink-0">
        <div className="flex items-center gap-3 min-w-0 pr-4">
          <div className="w-10 h-10 rounded-full bg-emerald-700 flex items-center justify-center text-white font-semibold text-sm shrink-0">
            {title ? title.charAt(0).toUpperCase() : 'P'}
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-white leading-tight truncate max-w-md sm:max-w-xl">
              {title}
            </h4>
            <p className="text-xs text-gray-400 mt-0.5">
              Product Image Preview
            </p>
          </div>
        </div>

        {/* Top Control Action Icons */}
        <div className="flex items-center gap-2 text-gray-300 shrink-0">
          {/* Download button */}
          <button
            type="button"
            onClick={handleDownload}
            className="p-2 hover:text-white hover:bg-white/10 rounded-full transition-colors focus:outline-none"
            title="Download Image"
            aria-label="Download Image"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
          </button>

          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:text-white hover:bg-white/10 rounded-full transition-colors focus:outline-none ml-1"
            title="Close Preview (Esc)"
            aria-label="Close Preview"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Main Content Stage / Backdrop */}
      <div
        ref={backdropRef}
        onMouseDown={handleBackdropMouseDown}
        onMouseUp={handleBackdropMouseUp}
        className="relative flex-1 flex items-center justify-center min-h-0 overflow-hidden p-4 sm:p-6 cursor-default"
      >
        {/* Loading Spinner */}
        {isLoading && !hasError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white pointer-events-none z-10">
            <div className="w-9 h-9 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm font-medium text-gray-300">
              Loading image...
            </p>
          </div>
        )}

        {/* Error Fallback */}
        {hasError ? (
          <div className="p-8 bg-[#202c33] rounded-2xl flex flex-col items-center gap-4 text-white max-w-sm text-center shadow-2xl border border-gray-700/50">
            <div className="w-14 h-14 rounded-full bg-gray-800/80 flex items-center justify-center text-gray-400">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-200">
                Image preview unavailable
              </p>
              <p className="text-xs text-gray-400 mt-1">
                The image could not be loaded or the URL has expired.
              </p>
            </div>
            <button
              type="button"
              onClick={handleDownload}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-xl text-xs font-semibold transition-colors"
            >
              Try Download
            </button>
          </div>
        ) : (
          /* Large Centered Image */
          <img
            src={imageUrl}
            alt={title || 'Product Image'}
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setIsLoading(false);
              setHasError(true);
            }}
            className={`max-h-full max-w-full object-contain rounded-lg shadow-2xl transition-opacity duration-300 ${
              isLoading ? 'opacity-0' : 'opacity-100'
            }`}
          />
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default ImagePreviewModal;
